import React, { useEffect, useRef, useState } from 'react';

const BracketBox = ({
  noZoom,
  color = '#07510c',
  thickness = '2px',
  width = '20%',
  lensSize = 200,
  lensVisible = true,
  imageRef,
  extraSrc,
  containRef,
  info,
  infoBoxWidth = 100
}) => {
  const lensRef = useRef(null);
  const boxSizeRef = useRef(lensSize);
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const isResizing = useRef(false);
  const animationFrame = useRef(null);
  const currentEvent = useRef(null);
  const handleRef = useRef(null);
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(scale);
  const backgroundImageRef = useRef(extraSrc);

  useEffect(() => {

    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    const newSrc = extraSrc || imageRef.current?.src;

    if (!newSrc) return;

    backgroundImageRef.current = newSrc;

    // 🔥 Force refresh even without mouse move
    if (lensRef.current) {
      lensRef.current.style.backgroundImage = `url(${newSrc})`;
    }

    // Recalculate zoom position
    if (currentEvent.current) {
      updateBg(currentEvent.current);
    }
  }, [extraSrc]); // ✅ only depend on real state

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!noZoom) {
        if (event.key === '+') {
          setScale((prev) => Math.min(prev + 0.1, 10));
        } else if (event.key === '-') {
          setScale((prev) => Math.max(prev - 0.1, 0.5));
        }
        updateBg(currentEvent.current);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const updateBg = (e) => {
    cancelAnimationFrame(animationFrame.current);
    animationFrame.current = requestAnimationFrame(() => {
      const image = imageRef.current;
      const lens = lensRef.current;
      if (!image || !lens) return;

      const rect = image.getBoundingClientRect();
      const lensRect = lens.getBoundingClientRect();

      // 🎯 center of lens instead of mouse
      const centerX = lensRect.left + lens.offsetWidth / 2;
      const centerY = lensRect.top + lens.offsetHeight / 2;

      const x = centerX - rect.left;
      const y = centerY - rect.top;

      const currentScale = scaleRef.current;

      const zoomedWidth = rect.width * currentScale;
      const zoomedHeight = rect.height * currentScale;

      const lensWidth = lens.offsetWidth;
      const lensHeight = lens.offsetHeight;

      const backgroundX = x * currentScale - lensWidth / 2;
      const backgroundY = y * currentScale - lensHeight / 2;

      lens.style.backgroundImage = `url(${backgroundImageRef.current})`;
      lens.style.backgroundSize = `${zoomedWidth}px ${zoomedHeight}px`;
      lens.style.backgroundPosition = `-${backgroundX}px -${backgroundY}px`;
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      currentEvent.current = e;
      const container = containRef?.current;
      const lens = lensRef?.current;
      if (!lens || !container) return;

      const containerRect = container.getBoundingClientRect();

      if (isDragging.current) {
        const x = e.clientX - containerRect.left - dragOffset.current.x;
        const y = e.clientY - containerRect.top - dragOffset.current.y;
        lens.style.left = `${x}px`;
        lens.style.top = `${y}px`;
        updateBg(e);
      }

      if (isResizing.current) {
        const left = parseFloat(lens.style.left || '0');
        const top = parseFloat(lens.style.top || '0');
        const x = e.clientX - containerRect.left;
        const y = e.clientY - containerRect.top;

        const newWidth = Math.max(50, x - left);
        const newHeight = Math.max(30, y - top);
        boxSizeRef.current = newWidth;
        lens.style.width = `${newWidth}px`;
        lens.style.height = `${newHeight}px`;
        updateBg(e);
      }

      if (draggingHandle.current) {
        const lensRect = lens.getBoundingClientRect();
        const centerX = lensRect.left + lens.offsetWidth / 2;
        const deltaX = e.clientX - centerX;

        // Adjust zoom based on how far the handle is dragged from center
        const newScale = Math.min(10, Math.max(0.5, 2 + deltaX / 100));
        setScale(newScale);
        updateBg(e);

        const handle = handleRef.current;
        if (handle) {
          handle.style.left = `${lens.offsetWidth / 2 + deltaX}px`;
        }
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
      draggingHandle.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [containRef, imageRef]);

  const handleMouseDown = (e) => {
    if (!lensRef.current) return;
    const lensRect = lensRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - lensRect.left,
      y: e.clientY - lensRect.top,
    };
    isDragging.current = true;
  };

  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    isResizing.current = true;
  };

  const draggingHandle = useRef(false);
  const handleZoomDragStart = (e) => {
    e.stopPropagation();
    draggingHandle.current = true;
  };

  const bracketStyle = {
    position: 'absolute',
    height: '100%',
    width,
    borderTop: `${thickness} solid ${color}`,
    borderBottom: `${thickness} solid ${color}`,
  };

  return (
    <>
      <div
        ref={lensRef}
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          top: '100px',
          left: '100px',
          width: `${lensSize}px`,
          height: `${lensSize / 2}px`,
          pointerEvents: 'auto',
          display: lensVisible ? 'block' : 'none',
          backgroundRepeat: 'no-repeat',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: 8,
          zIndex: 10,
          cursor: isResizing.current ? 'nwse-resize' : 'move',
          transition: 'background-size 0.40s ease-out, background-position 0.20s linear',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            background: `${color}`,
            color: '#000',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 11,
            zIndex: 99,
            right: 0,
            textAlign: 'center',
            width: infoBoxWidth,
            margin: 'auto'
          }}
        >
          {info ? info : <span>Zoom: {scale.toFixed(1)}x</span>}
        </div>
        {/* Brackets */}
        <div
          style={{
            ...bracketStyle,
            left: 0,
            borderLeft: `${thickness} solid ${color}`,
            borderRadius: '8px 0 0 8px',
          }}
        />
        <div
          style={{
            ...bracketStyle,
            right: 0,
            borderRight: `${thickness} solid ${color}`,
            borderRadius: '0 8px 8px 0',
          }}
        />

        {/* Resize Handle */}
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            right: -20,
            bottom: -20,
            width: 40,
            height: 40,
            borderRadius: 4,
            cursor: 'nwse-resize',
          }}
        >
          <div
            onMouseDown={handleResizeMouseDown}
            style={{
              width: 20,
              height: 20,
              background: color,
              borderRadius: 4,
              cursor: 'nwse-resize',
            }}
          />
        </div>

        {/* Zoom Handle Line */}
        {!noZoom && <div
          ref={handleRef}
          onMouseDown={handleZoomDragStart}
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '50%',
            width: 12,
            height: 12,
            background: 'white',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            border: `2px solid ${color}`,
            zIndex: 20,
            cursor: 'ew-resize',
          }}
        />}
      </div>
    </>
  );
};

export default BracketBox;

